import React, { useEffect, useMemo, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';
import { buildActivityRows } from '../lib/activityRows.js';
import { settlementRows } from '../lib/settlementRows.js';
import { dateLabel } from '../lib/useInvestmentData.js';
import { ActivityLedger } from './Activity.jsx';
import { AdminPulse } from './AdminPulse.jsx';

// Admin Activity — every household's ledger in one place (owner request
// 2026-09-22: "pull in all activity from all investors across all cards and
// all accounts, similar to what an individual investor gets, filterable with
// all filters"). Reached from the admin overview's "All activity" button and
// the #activity hash while NOT acting as anyone.
//
// Rows are shaped by the same builder the investor's Activity tab uses
// (activityRows.js), so a movement reads identically on both surfaces; the
// only additions are the household on each row and the Who filter.
//
// Attribution of a value-less movement: the owner of its source account,
// unless that account is Kareem's and the destination belongs to an
// investor (a payout, a bridge back) — then the investor. Kareem's own
// household activity stays Kareem's.

const initialsOf = (name) => String(name || '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

// PostgREST answers at most 1000 rows per request — the ledger sat at
// exactly "1000 value events" the first evening, silently missing everything
// older. Page with .range() until a page comes back short. `make` returns a
// FRESH builder each time (builders are single-use).
export async function fetchAll(make, pageSize = 1000) {
  const out = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await make().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = data || [];
    out.push(...rows);
    if (rows.length < pageSize) break;
  }
  return out;
}

export function useAdminActivity() {
  const [state, setState] = useState({ status: 'loading', events: [], members: [], settlements: [], households: 0, pool: null, enrolled: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) { setState({ status: 'error', events: [], members: [], settlements: [], households: 0, error: 'no supabase' }); return; }
      try {
        // Pool positions (v2_pool_position: every household for the admin) feed
        // the pulse's red flag. Tolerant — without them the pulse just shows
        // no pool column figures.
        const [membersRes, accountsRes, kindsRes, stepsRes, settlesRes, eventRows, moveRows, poolRes, enrollRes] = await Promise.all([
          sb.from('members').select('id, name, display_name, managed_by_member_id, active, color'),
          sb.from('financial_accounts').select('id, member_id, account_kind'),
          sb.from('inv_account_kinds').select('name, label'),
          sb.from('steps').select('id, source_kind, dest_kind'),
          sb.from('investment_settlements').select('id, enrolled_member_id, amount, settle_date, status, direction, kind, payment_method, memo, created_by_role, created_by_member_id'),
          fetchAll(() => sb.from('value_events').select('*').is('voided_at', null).order('event_date', { ascending: false }).order('id', { ascending: false })),
          fetchAll(() => sb.from('money_movements')
            .select('id, source_account_id, dest_account_id, amount, movement_date, created_at, status, created_by_role, created_by_member_id')
            .order('movement_date', { ascending: false }).order('id', { ascending: false })),
          Promise.resolve(sb.rpc('v2_pool_position')).then((r) => r, () => ({ data: null })),
          // The pulse lists investors only — not every member of the app.
          Promise.resolve(sb.from('module_enrollment').select('member_id, enabled').eq('module_name', 'investment')).then((r) => r, () => ({ data: null })),
        ]);
        const firstErr = [membersRes, accountsRes, kindsRes, stepsRes, settlesRes].find((r) => r.error);
        if (firstErr) throw firstErr.error;
        const eventsRes = { data: eventRows };
        const movesRes = { data: moveRows };

        const membersRaw = membersRes.data || [];
        const members = membersRaw.map((m) => ({
          id: m.id,
          name: m.display_name || m.name || m.id,
          initials: initialsOf(m.display_name || m.name),
          color: m.color || '#6366f1',
          household: m.managed_by_member_id || m.id,
          active: m.active !== false,
        }));
        const householdOfMember = Object.fromEntries(members.map((m) => [m.id, m.household]));
        const householdOf = (id) => householdOfMember[id] || id || null;

        const kindOfAccount = {};
        const memberOfAccount = {};
        (accountsRes.data || []).forEach((a) => { kindOfAccount[a.id] = a.account_kind; memberOfAccount[a.id] = a.member_id; });
        const kindMeta = Object.fromEntries((kindsRes.data || []).map((k) => [k.name, k.label]));
        const kindLabel = (slug) => (slug ? (kindMeta[slug] || slug.replace(/_/g, ' ')) : '');
        const stepsById = Object.fromEntries((stepsRes.data || []).map((s) => [s.id, s]));

        const movements = movesRes.data || [];
        const mvAccounts = {};
        movements.forEach((m) => { mvAccounts[m.id] = m; });

        const memberOf = (mv) => {
          const ms = memberOfAccount[mv.source_account_id] || null;
          const md = memberOfAccount[mv.dest_account_id] || null;
          if (ms && ms !== 'kareem') return ms;
          return md || ms || null;
        };

        const events = buildActivityRows({
          events: eventsRes.data || [],
          movements,
          mvAccounts,
          ctx: { kindOfAccount, stepsById, kindLabel, dateLabel, memberOf, householdOf },
        });
        // Cash settlements both ways (2026-10-23), one row builder with the
        // investor's Activity tab.
        settlementRows(settlesRes.data || [], { dateLabel }).forEach((row) => events.push(row));
        events.sort((a, b) => String(b.eventDate || '').localeCompare(String(a.eventDate || '')));
        const households = new Set(events.map((e) => e.household).filter(Boolean)).size;
        const pool = Array.isArray(poolRes && poolRes.data) && !poolRes.error
          ? Object.fromEntries(poolRes.data.filter((p) => p && p.household).map((p) => [p.household, p]))
          : null;
        const enrolled = Array.isArray(enrollRes && enrollRes.data) && !enrollRes.error
          ? enrollRes.data.filter((e) => e.enabled !== false).map((e) => e.member_id)
          : null;
        if (!cancelled) setState({ status: 'ready', events, members, settlements: settlesRes.data || [], households, pool, enrolled, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: 'error', events: [], members: [], settlements: [], households: 0, error: e?.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChange = () => setReloadKey((k) => k + 1);
    window.addEventListener('finops:enrollment-changed', onChange);
    return () => window.removeEventListener('finops:enrollment-changed', onChange);
  }, []);

  return state;
}

export default function AdminActivity({ navigate, beginActAs }) {
  const { status, events, members, settlements, households, pool, enrolled, error } = useAdminActivity();
  const byId = useMemo(() => Object.fromEntries((members || []).map((m) => [m.id, m])), [members]);
  const back = () => { if (typeof navigate === 'function') navigate('home', {}); };
  const valueEvents = useMemo(() => events.filter((e) => !e.noValue).length, [events]);

  if (status === 'loading') {
    return <div className="page"><div className="activity-empty">Loading activity across every household…</div></div>;
  }
  if (status === 'error') {
    return (
      <div className="page">
        <div className="page-title-row">
          <div><div className="page-eyebrow">Investment · Admin</div><h1 className="page-title">All activity</h1></div>
          <button type="button" className="btn-ghost" onClick={back}>← Overview</button>
        </div>
        <div className="activity-empty">Couldn't load the ledger: {String(error)}</div>
      </div>
    );
  }

  const header = (
    <div className="page-title-row">
      <div>
        <div className="page-eyebrow"><span className="dot"></span> Investment · Admin</div>
        <h1 className="page-title">All activity</h1>
        <p className="page-sub">
          {valueEvents} value event{valueEvents === 1 ? '' : 's'} and {events.length - valueEvents} value-less movement{events.length - valueEvents === 1 ? '' : 's'} across {households} household{households === 1 ? '' : 's'} · every card and account.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn-ghost" onClick={back}>← Overview</button>
      </div>
    </div>
  );

  // A row opens its household in act-as, landing on THAT investor's Activity
  // tab — the same ledger, scoped, with the household's own admin controls a
  // tab away.
  const openRow = typeof beginActAs === 'function'
    ? (e) => { const target = e.household || e.member; if (target) beginActAs(target, 'activity'); }
    : undefined;

  return (
    <ActivityLedger
      events={events}
      members={members}
      mode="admin"
      header={header}
      emptyText="No movements logged in any household yet."
      onRowClick={openRow}
      settlements={settlements}
      aboveFilters={events.length ? ({ focus }) => (
        <AdminPulse rows={events} byId={byId} pool={pool} enrolled={enrolled} onPick={(id, from) => focus({ who: id, from })} />
      ) : undefined}
    />
  );
}
